// =============================================================================
// Distribution Email Notification Modal
// =============================================================================
// Modal shown after publishing a distribution to optionally send email notifications
// =============================================================================

import { useState } from 'react';
import { XMarkIcon, EnvelopeIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

// =============================================================================
// Types
// =============================================================================

export interface DistributionEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (subject: string, message: string) => Promise<void>;
  distribution: {
    dealName: string;
    totalAmount: number;
    distributionDate: string;
    type: string;
    investorCount: number;
  };
}

// =============================================================================
// Component
// =============================================================================

export function DistributionEmailModal({
  isOpen,
  onClose,
  onSend,
  distribution,
}: DistributionEmailModalProps) {
  const [subject, setSubject] = useState(
    `Distribution Processed: ${distribution.dealName}`
  );
  const [message, setMessage] = useState(
    `Your ${getTypeLabel(distribution.type).toLowerCase()} distribution for ${distribution.dealName} has been processed and is on its way. The distribution was recorded on ${formatDate(distribution.distributionDate)}.\n\nPlease log into your investor portal to view the full details of this distribution.`
  );
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    setError(null);
    setIsSending(true);
    try {
      await onSend(subject, message);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send notifications');
    } finally {
      setIsSending(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all">
          {/* Header */}
          <div className="border-b border-gray-100 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <EnvelopeIcon className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Distribution Published
                  </h2>
                  <p className="text-sm text-gray-500">
                    Send email notification to investors?
                  </p>
                </div>
              </div>
              <button
                onClick={handleSkip}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-4">
            {/* Distribution Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Property</p>
                  <p className="font-medium text-gray-900">{distribution.dealName}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="font-medium text-green-600">
                    {formatCurrency(distribution.totalAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Distribution Date</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(distribution.distributionDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Recipients</p>
                  <p className="font-medium text-gray-900">
                    {distribution.investorCount} investor{distribution.investorCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Email Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Subject
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter email subject..."
              />
            </div>

            {/* Email Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message to Investors
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter your message to investors..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Each investor will receive a personalized email with their individual distribution amount.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
            <div className="flex justify-between gap-3">
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={isSending}
              >
                Skip Notification
              </Button>
              <Button
                onClick={handleSend}
                disabled={isSending || !subject.trim() || !message.trim()}
                className="flex items-center gap-2"
              >
                {isSending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Sending...
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4" />
                    Send to {distribution.investorCount} Investor{distribution.investorCount !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Helper Functions
// =============================================================================

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function getTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    QUARTERLY: 'Quarterly',
    ANNUAL: 'Annual',
    SPECIAL: 'Special',
    RETURN_OF_CAPITAL: 'Return of Capital',
    REFINANCE: 'Refinance',
    SALE: 'Sale',
    OTHER: 'Other',
  };
  return labels[type] || type;
}

export default DistributionEmailModal;
